/**
 * Pure helpers for the sn-free-tool Signature Generator: font list, undo/redo history, pixel
 * processing for uploaded signatures and file validation.
 *
 * No DOM and no Stencil. Pixel buffers are plain RGBA arrays in the layout of `ImageData.data`, so
 * the component passes canvas data straight through; crop rectangles are in preview CSS pixels.
 *
 * Exports: SIGNATURE_FONTS, SnapshotHistory, inkToAlpha, alphaBounds, CropHandle, rectFromPoints,
 * moveRect, resizeRect, clampRect, validateFile.
 */

export interface SignatureFont {
  /** CSS font-family, declared in source/fonts.css. */
  family: string;
  /** Preview size in px from the design; the scripts differ a lot in x-height. */
  size: number;
}

/** Order and sizes as in the Type tab of the design (two columns, three rows). */
export const SIGNATURE_FONTS: readonly SignatureFont[] = [
  { family: 'Kalam', size: 49 },
  { family: 'Carattere', size: 62 },
  { family: 'Playwrite US Trad', size: 41 },
  { family: 'Marck Script', size: 49 },
  { family: 'Mynerve', size: 49 },
  { family: 'Meddon', size: 36 },
];

/**
 * Undo/redo over immutable snapshots. Clearing the canvas is recorded like any other change, so
 * "Clear" can itself be undone.
 */
export class SnapshotHistory<T> {
  private past: T[] = [];
  private future: T[] = [];

  constructor(private current: T) {}

  get value(): T { return this.current; }
  get canUndo(): boolean { return this.past.length > 0; }
  get canRedo(): boolean { return this.future.length > 0; }

  push(next: T): T {
    this.past.push(this.current);
    this.current = next;
    this.future = [];
    return this.current;
  }

  undo(): T {
    const previous = this.past.pop();
    if (previous === undefined) return this.current;
    this.future.push(this.current);
    this.current = previous;
    return this.current;
  }

  redo(): T {
    const next = this.future.pop();
    if (next === undefined) return this.current;
    this.past.push(this.current);
    this.current = next;
    return this.current;
  }
}

type Pixels = Uint8ClampedArray | number[];

/**
 * Turns a photo of a signature into ink on a transparent background.
 *
 * `contrast` is the slider value, 0–100. It moves the luminance cut-off: pixels lighter than the
 * cut-off become transparent, darker ones keep their colour (a red pen stays red) and get an alpha
 * that ramps up over a short band, so stroke edges stay smooth instead of turning jagged.
 */
export function inkToAlpha(src: Pixels, contrast: number): Uint8ClampedArray {
  const cutoff = 90 + Math.max(0, Math.min(100, contrast)) * 1.5; // 90…240
  const band = 40;
  const out = new Uint8ClampedArray(src.length);
  for (let i = 0; i < src.length; i += 4) {
    const r = src[i], g = src[i + 1], b = src[i + 2], a = src[i + 3];
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    const ink = luminance >= cutoff ? 0 : Math.min(1, (cutoff - luminance) / band);
    out[i] = r;
    out[i + 1] = g;
    out[i + 2] = b;
    out[i + 3] = Math.round(ink * a);
  }
  return out;
}

export interface Rect { x: number; y: number; width: number; height: number }

/** Smallest rectangle holding every pixel with visible alpha, or null for an empty image. */
export function alphaBounds(data: Pixels, width: number, height: number, minAlpha = 8): Rect | null {
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] < minAlpha) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
}

export type CropHandle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** Rectangle spanned by two points in any order, kept inside width×height ("rubber band"). */
export function rectFromPoints(x1: number, y1: number, x2: number, y2: number, width: number, height: number): Rect {
  const left = clamp(Math.min(x1, x2), 0, width);
  const top = clamp(Math.min(y1, y2), 0, height);
  const right = clamp(Math.max(x1, x2), 0, width);
  const bottom = clamp(Math.max(y1, y2), 0, height);
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/** Shifts a rectangle by dx/dy without letting it leave the width×height area; its size is kept. */
export function moveRect(rect: Rect, dx: number, dy: number, width: number, height: number): Rect {
  return {
    ...rect,
    x: clamp(rect.x + dx, 0, Math.max(0, width - rect.width)),
    y: clamp(rect.y + dy, 0, Math.max(0, height - rect.height)),
  };
}

/**
 * Drags one edge or corner of a rectangle. The opposite edge stays put, the dragged one stops at
 * the area bounds and never comes closer than `min` to the opposite one, so the box cannot flip.
 */
export function resizeRect(rect: Rect, handle: CropHandle, dx: number, dy: number, width: number, height: number, min: number): Rect {
  let left = rect.x;
  let top = rect.y;
  let right = rect.x + rect.width;
  let bottom = rect.y + rect.height;
  if (handle.includes('w')) left = clamp(left + dx, 0, right - min);
  if (handle.includes('e')) right = clamp(right + dx, left + min, width);
  if (handle.includes('n')) top = clamp(top + dy, 0, bottom - min);
  if (handle.includes('s')) bottom = clamp(bottom + dy, top + min, height);
  return { x: left, y: top, width: right - left, height: bottom - top };
}

/** Fits a rectangle into width×height after the area shrank (a resize), keeping it at least `min`. */
export function clampRect(rect: Rect, width: number, height: number, min: number): Rect {
  const w = clamp(rect.width, Math.min(min, width), width);
  const h = clamp(rect.height, Math.min(min, height), height);
  return { x: clamp(rect.x, 0, width - w), y: clamp(rect.y, 0, height - h), width: w, height: h };
}

export type FileProblem = 'type' | 'size';

/** Checks a picked or dropped file against an accept list of MIME types and a size cap. */
export function validateFile(
  file: { type: string; size: number },
  accept: readonly string[],
  maxBytes: number,
): FileProblem | null {
  if (!accept.includes(file.type)) return 'type';
  if (file.size > maxBytes) return 'size';
  return null;
}
